import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  OnDestroy,
  output,
  ViewChild,
} from '@angular/core';
import Quill from 'quill';
import type { QuillOptions } from 'quill';

let uiRichEditorUid = 0;

/**
 * Átomo: editor de texto enriquecido basado en Quill.
 * Mismo patrón de integración que ui-date-input con Flatpickr.
 * Sin HTTP ni stores.
 */
@Component({
  selector: 'ui-rich-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './rich-editor.component.html',
  styleUrl: './rich-editor.component.scss',
  host: {
    class: 'ui-rich-editor-host',
    '[class.ui-rich-editor-host--block]': 'block()',
  },
})
export class UiRichEditorComponent implements AfterViewInit, OnDestroy {
  @ViewChild('editorContainer') private readonly editorContainer!: ElementRef<HTMLDivElement>;

  readonly value = model<string>('');
  readonly label = input('');
  readonly placeholder = input('Escribe aquí...');

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);

  readonly minHeight = input('12rem');
  readonly maxHeight = input<string | undefined>(undefined);

  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly valueChange = output<string>();
  readonly blurred = output<void>();
  readonly focused = output<void>();

  protected readonly generatedId = `ui-rich-editor-${++uiRichEditorUid}`;
  protected readonly inputId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly messageId = computed(() => `${this.inputId()}-message`);

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );
  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly isLocked = computed(
    () => this.disabled() || this.readonly(),
  );

  private quill: Quill | null = null;
  private skipNextValueEffect = false;

  constructor() {
    effect(() => {
      const v = this.value();
      const locked = this.isLocked();
      if (!this.quill || this.skipNextValueEffect) {
        this.skipNextValueEffect = false;
        return;
      }
      if (locked) return;
      const current = this.quill.getSemanticHTML();
      const next = v || '';
      if (current !== next) {
        this.applyHtml(next);
      }
    });

    effect(() => {
      if (!this.quill) return;
      this.quill.enable(!this.isLocked());
    });
  }

  ngAfterViewInit(): void {
    const options: QuillOptions = {
      theme: 'snow',
      placeholder: this.placeholder(),
      readOnly: false,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link', 'image', 'blockquote', 'code-block'],
          ['clean'],
        ],
      },
    };

    this.quill = new Quill(this.editorContainer.nativeElement, options);

    if (!this.isLocked() && this.value()) {
      this.applyHtml(this.value());
    }

    this.quill.enable(!this.isLocked());

    this.quill.on('text-change', () => {
      if (!this.quill || this.isLocked()) return;
      const html = this.quill.getSemanticHTML();
      const emitted = !html || html === '<p><br></p>' || html === '<p></p>' ? '' : html;
      this.skipNextValueEffect = true;
      this.value.set(emitted);
      this.valueChange.emit(emitted);
    });

    this.quill.on('selection-change', (range) => {
      if (range) {
        this.focused.emit();
      } else {
        this.blurred.emit();
      }
    });
  }

  ngOnDestroy(): void {
    this.quill = null;
  }

  private applyHtml(html: string): void {
    if (!this.quill) return;
    const delta = this.quill.clipboard.convert({ html });
    this.quill.setContents(delta, 'silent');
  }
}
